import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import rolesService from '../services/rolesService';

const PERMISSION_MAP = {
  addUser: 'Add Users',
  editUser: 'Edit Users',
  viewUser: 'View Users',
  deleteUser: 'Delete Users',
  addProperty: 'Add Properties',
  editProperty: 'Edit Properties',
  viewProperty: 'View Properties',
  deleteProperty: 'Delete Properties',
  addBooking: 'Add Bookings',
  editBooking: 'Edit Bookings',
  viewBooking: 'View Bookings',
  deleteBooking: 'Delete Bookings',
  confirmBooking: 'Confirm Bookings',
  viewFinancialReports: 'View Financial Reports',
  exportFinancialData: 'Export Financial Data',
  manageExpenses: 'Manage Expenses',
  viewRevenue: 'View Revenue',
  sendGuestEmails: 'Send Guest Emails',
  viewGuestDetails: 'View Guest Details',
  manageGuestProfiles: 'Manage Guest Profiles',
  viewSystemSettings: 'View System Settings',
  editSystemSettings: 'Edit System Settings',
  manageSystemSettings: 'Manage System Settings',
  exportUsers: 'Export Users',
  exportBookings: 'Export Bookings',
  exportProperties: 'Export Properties',
  exportReports: 'Export Reports',
  manageEmailTemplates: 'Manage Email Templates',
  manageNotifications: 'Manage Notifications',
  viewAnalytics: 'View Analytics',
  generateReports: 'Generate Reports',
};

const PERMISSION_GROUPS = [
  { label: 'User Management', keys: ['addUser', 'editUser', 'viewUser', 'deleteUser'] },
  { label: 'Property Management', keys: ['addProperty', 'editProperty', 'viewProperty', 'deleteProperty'] },
  { label: 'Booking Management', keys: ['addBooking', 'editBooking', 'viewBooking', 'deleteBooking', 'confirmBooking'] },
  { label: 'Financial', keys: ['viewFinancialReports', 'exportFinancialData', 'manageExpenses', 'viewRevenue'] },
  { label: 'Guest Communication', keys: ['sendGuestEmails', 'viewGuestDetails', 'manageGuestProfiles'] },
  { label: 'System Settings', keys: ['viewSystemSettings', 'editSystemSettings', 'manageSystemSettings'] },
  { label: 'Export Permissions', keys: ['exportUsers', 'exportBookings', 'exportProperties', 'exportReports'] },
  { label: 'Content Management', keys: ['manageEmailTemplates', 'manageNotifications'] },
  { label: 'Analytics', keys: ['viewAnalytics', 'generateReports'] },
];

const EMPTY_PERMISSIONS = Object.fromEntries(Object.keys(PERMISSION_MAP).map(k => [k, false]));

const RoleCard = ({ role, onEdit, onDelete }) => (
  <div className="bg-card rounded-2xl border border-border shadow-sm p-6 space-y-4">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
            <Icon icon="lucide:shield" className="text-xl text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-bold">{role.name}</h3>
            <p className="text-xs text-muted-foreground">{role.description}</p>
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Permissions</p>
          <div className="flex flex-wrap gap-2">
            {(role.permissions || []).slice(0, 8).map((key, i) => (
              <span key={i} className="px-2 py-1 bg-primary/10 text-primary text-[10px] font-bold rounded-lg">
                {PERMISSION_MAP[key] || key}
              </span>
            ))}
            {role.permissions?.length > 8 && (
              <span className="px-2 py-1 bg-muted text-muted-foreground text-[10px] font-bold rounded-lg">
                +{role.permissions.length - 8} more
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 ml-4">
        {role.isSystem ? (
          <span className="px-2 py-1 bg-muted text-muted-foreground text-[10px] font-bold rounded-lg">System</span>
        ) : (
          <>
            <button onClick={() => onEdit(role)} className="p-2 hover:bg-muted rounded-lg transition-colors" aria-label="Edit role">
              <Icon icon="lucide:edit" className="text-muted-foreground" />
            </button>
            <button onClick={() => onDelete(role.id)} className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-lg transition-colors" aria-label="Delete role">
              <Icon icon="lucide:trash-2" className="text-muted-foreground" />
            </button>
          </>
        )}
      </div>
    </div>
  </div>
);

const PermissionCheckbox = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl border border-border cursor-pointer hover:bg-muted/50 transition-colors">
    <input type="checkbox" checked={checked} onChange={onChange} className="w-4 h-4 text-primary rounded border-border" />
    <p className="text-sm font-bold">{label}</p>
  </label>
);

export default function UserRoles() {
  const [activeView, setActiveView] = useState('roles');
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingRole, setEditingRole] = useState(null);

  const [formData, setFormData] = useState({ name: '', description: '', permissions: { ...EMPTY_PERMISSIONS } });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await rolesService.fetchRoles();
      setRoles(data);
    } catch (err) {
      setError('Failed to load roles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingRole(null);
    setFormData({ name: '', description: '', permissions: { ...EMPTY_PERMISSIONS } });
    setActiveView('create');
  };

  const openEdit = (role) => {
    setEditingRole(role);
    const perms = { ...EMPTY_PERMISSIONS };
    (role.permissions || []).forEach(k => { if (k in perms) perms[k] = true; });
    setFormData({ name: role.name, description: role.description || '', permissions: perms });
    setActiveView('create');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    const permissions = Object.keys(formData.permissions).filter(k => formData.permissions[k]);
    setSaving(true);
    try {
      if (editingRole) {
        const updated = await rolesService.updateRole(editingRole.id, {
          name: formData.name,
          description: formData.description,
          permissions,
        });
        setRoles(prev => prev.map(r => r.id === updated.id ? updated : r));
      } else {
        const created = await rolesService.createRole({
          name: formData.name,
          description: formData.description,
          permissions,
        });
        setRoles(prev => [...prev, created]);
      }
      setActiveView('roles');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save role.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this role? This cannot be undone.')) return;
    try {
      await rolesService.deleteRole(id);
      setRoles(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete role.');
    }
  };

  const togglePermission = (key, value) => {
    setFormData(prev => ({ ...prev, permissions: { ...prev.permissions, [key]: value } }));
  };

  if (activeView === 'create') {
    return (
      <div className="space-y-8">
        <div className="bg-card rounded-2xl border border-border shadow-sm p-6 space-y-6">
          <h2 className="text-lg font-heading font-bold">{editingRole ? 'Edit Role' : 'Create New Role'}</h2>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Role Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Marketing Manager"
                className="w-full px-4 py-3 bg-muted border-none rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description of this role's responsibilities"
                className="w-full px-4 py-3 bg-muted border-none rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
              />
            </div>
          </div>

          <div className="space-y-6">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Permissions</p>
            {PERMISSION_GROUPS.map(group => (
              <div key={group.label} className="space-y-3">
                <p className="text-xs font-bold text-primary uppercase tracking-wider">{group.label}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {group.keys.map(key => (
                    <PermissionCheckbox
                      key={key}
                      label={PERMISSION_MAP[key]}
                      checked={formData.permissions[key]}
                      onChange={e => togglePermission(key, e.target.checked)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || !formData.name.trim()}
              className="bg-primary text-primary-foreground px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-primary/20 hover:opacity-90 transition-all disabled:opacity-50"
            >
              <Icon icon={saving ? 'lucide:loader-2' : 'lucide:check'} className={`text-lg ${saving ? 'animate-spin' : ''}`} />
              <span>{saving ? 'Saving...' : editingRole ? 'Save Changes' : 'Create Role'}</span>
            </button>
            <button
              onClick={() => setActiveView('roles')}
              className="bg-muted text-foreground px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 hover:bg-muted/80 transition-all"
            >
              <Icon icon="lucide:x" className="text-lg" />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-bold">User Roles & Permissions</h2>
          <p className="text-sm text-muted-foreground">Define access levels and permissions for different admin staff types.</p>
        </div>
        <button
          onClick={openCreate}
          className="bg-primary text-primary-foreground px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-primary/20 hover:opacity-90 transition-all"
        >
          <Icon icon="lucide:plus" className="text-lg" />
          <span>Create New Role</span>
        </button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-card rounded-2xl border border-border p-6 animate-pulse h-40" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {roles.map(role => (
            <RoleCard key={role.id} role={role} onEdit={openEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
}
